<template>
    <div class="trailer-search">
        <div class="trailer-search__input-wrap">
            <input
                v-model="query"
                type="text"
                class="trailer-search__input"
                aria-label="Wofür benötigen Sie einen Anhänger?"
                :class="{ 'has-error': error }"
                :placeholder="currentPlaceholder"
                :disabled="isLoading"
                @keydown.enter.prevent="search"
            />
            <button
                class="trailer-search__button"
                :disabled="isLoading || query.length < 3"
                @click="search"
            >
                <svg v-if="!isLoading" class="trailer-search__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <circle cx="10.5" cy="10.5" r="6.5"/>
                    <path d="m16 16 5 5"/>
                </svg>
                <span v-if="isLoading" class="trailer-search__spinner"></span>
                <span class="trailer-search__button-label">{{ isLoading ? 'Suche läuft…' : 'Anhänger finden' }}</span>
            </button>
        </div>

        <p v-if="error" class="trailer-search__error">{{ error }}</p>

        <div v-if="results.length" class="trailer-search__results">
            <h3 class="trailer-search__results-title">Passende Anhänger für Ihr Vorhaben</h3>
            <div class="trailer-search__grid">
                <TrailerSearchResultCard
                    v-for="result in results"
                    :key="result.id"
                    :trailer="result"
                />
            </div>
        </div>

        <div v-if="hasSearched && !isLoading" class="trailer-search__footer">
            <p v-if="!results.length && !error" class="trailer-search__empty">
                Leider konnten wir keinen passenden Anhänger finden.
            </p>
            <a :href="rentalPageUrl" class="trailer-search__all-link">
                <span class="trailer-search__all-link-label">Alle Mietanhänger ansehen</span>
                <BaseIcon name="ri-arrow-right-line" />
            </a>
        </div>
    </div>
</template>

<script setup>
import BaseIcon from "../component/BaseIcon.vue";
import { ref, onMounted, onUnmounted } from 'vue'
import axios from 'axios'
import TrailerSearchResultCard from '../component/TrailerSearchResultCard.vue'

const props = defineProps({
    rentalPageUrl: {
        type: String,
        default: '/vermietung',
    },
})

const placeholders = [
    'Gartenabfälle zum Kompostierwerk bringen',
    'Umzug mit vielen Möbeln',
    'Auto transportieren',
    'Kühlwaren für ein Event',
    'Motorrad in die Werkstatt bringen',
    'Bauschutt entsorgen',
]

const currentPlaceholder = ref('')
let typewriterTimeout = null
let placeholderIdx = 0
let charIdx = 0
let isDeleting = false

const TYPE_SPEED = 60
const DELETE_SPEED = 30
const PAUSE_AFTER_TYPE = 2000
const PAUSE_AFTER_DELETE = 400

function tick() {
    const full = placeholders[placeholderIdx]

    if (!isDeleting) {
        charIdx++
        currentPlaceholder.value = full.slice(0, charIdx)

        if (charIdx === full.length) {
            isDeleting = true
            typewriterTimeout = setTimeout(tick, PAUSE_AFTER_TYPE)
            return
        }
        typewriterTimeout = setTimeout(tick, TYPE_SPEED)
    } else {
        charIdx--
        currentPlaceholder.value = full.slice(0, charIdx)

        if (charIdx === 0) {
            isDeleting = false
            placeholderIdx = (placeholderIdx + 1) % placeholders.length
            typewriterTimeout = setTimeout(tick, PAUSE_AFTER_DELETE)
            return
        }
        typewriterTimeout = setTimeout(tick, DELETE_SPEED)
    }
}

onMounted(() => {
    tick()
})

onUnmounted(() => {
    clearTimeout(typewriterTimeout)
})

const query = ref('')
const results = ref([])
const isLoading = ref(false)
const error = ref(null)
const hasSearched = ref(false)

async function search() {
    const q = query.value.trim()
    if (q.length < 3 || isLoading.value) return

    isLoading.value = true
    error.value = null
    results.value = []

    try {
        const response = await axios.post('/search/trailer', { query: q })
        const data = response.data

        if (data.error) {
            error.value = data.error
        } else {
            results.value = data.results || []
        }
    } catch {
        error.value = 'Die Suche ist vorübergehend nicht verfügbar. Bitte versuchen Sie es später.'
    } finally {
        isLoading.value = false
        hasSearched.value = true
    }
}
</script>
